import { useEffect, useRef } from 'react';
import { toDateString } from '@koob/dates';
import { ChildSyncSnapshot, resolveChildSync } from './folderFormState';
import type { FolderFormValues } from './types';

export interface FolderChildDataSyncProps {
  values: FolderFormValues;
  setFieldValue: (field: string, value: any) => void;
}

/**
 * Keeps a child's birthdate consistent between the search data (travellers
 * step) and the traveller details card while both stay editable: whichever
 * side was edited last is mirrored onto the other.
 *
 * Renders nothing — it only needs to sit inside the Formik render tree.
 */
export function FolderChildDataSync({
  values,
  setFieldValue,
}: FolderChildDataSyncProps) {
  const lastSynced = useRef<ChildSyncSnapshot[]>([]);

  // No dependency list: the effect diffs both sides itself and writes only
  // when they actually moved, so a re-render costs one comparison per child.
  useEffect(() => {
    const next: ChildSyncSnapshot[] = [];

    values.children.forEach((child, j) => {
      const index = values.adults + j;
      const current: ChildSyncSnapshot = {
        detail: toDateString(values.travelers[index]?.birthdate) ?? '',
        search: child.birthdate ?? '',
      };

      const action = resolveChildSync(lastSynced.current[j], current);

      if (action.type === 'confirmBirthdate') {
        setFieldValue(`children[${j}]`, action.entry);
        // The traveller record carries the same approximation flag, and an
        // exact date leaves no age behind.
        setFieldValue(`travelers[${index}].ageIsExact`, true);
        setFieldValue(`travelers[${index}].age`, undefined);
        next[j] = { detail: current.detail, search: current.detail };
        return;
      }

      next[j] = current;
    });

    lastSynced.current = next;
  });

  return null;
}
